"use client"

import { useRouter } from "@bprogress/next/app";
import { Box, Button, colors, Menu, MenuItem, MenuItemProps, NoSsr, Skeleton, Typography, useTheme } from "@mui/material";
import { ArrowDownWideNarrow, BanknoteArrowDown, BanknoteArrowUp, ChevronDown, Heart, Sparkles } from "lucide-react";
import { usePathname, useSearchParams } from "next/navigation";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { requestPlpHeaderScroll } from "./scrollToPlpHeader";

// "inline" trades the standalone button for a label plus a compact dropdown, so the
// toolbar can spend the freed width on the result count and the active filter chips.
export default function PlpSort({ variant = "button" }: { variant?: "button" | "inline" }) {
    const searchParams = useSearchParams()
    const pathname = usePathname();
    const router = useRouter()
    const theme = useTheme()
    const searchParamsKey = searchParams.toString()

    const sortParam = searchParams.get('sort') ?? 'suggested'

    // Picking a sort sets this ahead of the navigation so the label answers the
    // click straight away. Anything that moves the URL on its own — back, forward,
    // a chip that clears the sort — has to be followed back the other way.
    const [value, setValue] = useState<string>(sortParam)
    useEffect(() => setValue(sortParam), [sortParam])

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    }, []);
    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const onPickSort = useCallback((sort: string) => () => {
        setValue(sort)
        handleClose()

        const current = new URLSearchParams(Array.from(searchParams.entries()));

        if (sort === "suggested") {
            current.delete('sort')
        } else {
            current.set('sort', sort)
        }
        current.delete('lp')
        current.delete('page')
        const search = current.toString();
        const query = search ? `?${search}` : "";

        if (search === searchParamsKey) return;

        requestPlpHeaderScroll();
        router.push(`${pathname}${query}`, { scroll: false });
    }, [searchParams, searchParamsKey, pathname])


    const translateValue = useMemo(() => (item: string) => {
        switch (item) {
            case "newest": return "جدیدترین";
            case "most-expensive": return "گران‌ترین";
            case "cheapest": return "ارزان‌ترین";
            default: return 'پیشنهاد وحدت'
        }
    }, [])

    const isInline = variant === "inline";

    return (
        <NoSsr fallback={
            isInline
                ? <Skeleton variant="rounded" sx={{
                    height: 34,
                    // The label only shows from md, so a phone placeholder that reserves
                    // room for it pushes the filter trigger onto a second row until
                    // hydration swaps it out.
                    width: { xs: 112, md: 200 },
                }} />
                : <Skeleton variant="rounded" height={58} width={180} sx={{
                    display: 'inline-block',
                    mr: 1,
                    [theme.breakpoints.down('md')]: {
                        width: `150px !important`,
                        height: `48px !important`
                    },
                }} />
        }>
            {isInline ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                    {/* No room for the label on a phone once the count and the filter trigger
                        share the row; the value plus its chevron carries the control there. */}
                    <Typography component="span" sx={{
                        display: { xs: 'none', md: 'inline' },
                        flexShrink: 0,
                        fontSize: 13,
                        color: colors.grey[700],
                        whiteSpace: 'nowrap',
                    }}>
                        مرتب سازی بر اساس
                    </Typography>
                    <Button
                        id="sort-positioned-button"
                        aria-controls={open ? 'sort-positioned-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? 'true' : undefined}
                        onClick={handleClick}
                        variant="text"
                        color="inherit"
                        endIcon={<ChevronDown size={16} />}
                        sx={{
                            minWidth: 0,
                            px: 1,
                            py: 0.5,
                            fontSize: 14,
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {translateValue(value)}
                    </Button>
                </Box>
            ) : (
            <Button
                id="sort-positioned-button"
                aria-controls={open ? 'sort-positioned-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}
                variant="outlined"
                color="inherit"
                endIcon={<ArrowDownWideNarrow />}
                sx={{
                    height: 58,
                    mr: 1,
                    width: 180,
                    justifyContent: 'space-between',
                    [theme.breakpoints.down('md')]: {
                        width: 150,
                        height: 48
                    },
                }}>
                <Box sx={{ textAlign: 'left' }}>
                    <Typography component="span" sx={{ fontSize: 10, color: colors.grey[700], display: 'block' }}>
                        مرتب سازی بر اساس
                    </Typography>
                    {translateValue(value)}
                </Box>
            </Button>
            )}
            <Menu
                id="demo-positioned-menu"
                aria-labelledby="demo-positioned-button"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                disableScrollLock
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <StyledMenuItem onClick={onPickSort('suggested')}>
                    <Typography component="span">پیشنهاد وحدت</Typography>
                    <Sparkles color={colors.amber[600]} />
                </StyledMenuItem>
                <StyledMenuItem onClick={onPickSort('newest')}>
                    <Typography component="span">جدیدترین</Typography>
                    <Heart color={colors.red[800]} />
                </StyledMenuItem>
                <StyledMenuItem onClick={onPickSort('most-expensive')}>
                    <Typography component="span">گران ترین</Typography>
                    <BanknoteArrowUp />
                </StyledMenuItem>
                <StyledMenuItem onClick={onPickSort('cheapest')}>
                    <Typography component="span">ارزان ترین</Typography>
                    <BanknoteArrowDown />
                </StyledMenuItem>
            </Menu>
        </NoSsr>
    )
}

const StyledMenuItem = ({ children, ...rest }: { children: ReactNode } & MenuItemProps) => {
    return <MenuItem sx={{
        width: 180,
        height: 58,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    }} {...rest}>{children}</MenuItem>
}
